import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Language } from '../types/i18n';
import { translations } from '../i18n/translations';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  availableLanguages: { code: Language; name: string; flag: string }[];
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const availableLanguages = [
  { code: 'en' as Language, name: 'English', flag: '🇺🇸' },
  { code: 'ko' as Language, name: '한국어', flag: '🇰🇷' },
  { code: 'ja' as Language, name: '日本語', flag: '🇯🇵' },
  { code: 'zh' as Language, name: '中文', flag: '🇨🇳' },
  { code: 'ru' as Language, name: 'Русский', flag: '🇷🇺' }
];

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Detect language from localStorage or browser preference
  const getInitialLanguage = (): Language => {
    const saved = localStorage.getItem('mv3-language') as Language;
    if (saved && availableLanguages.some(lang => lang.code === saved)) {
      return saved;
    }

    // Default to English always, ignoring browser language
    return 'en';
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage());

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('mv3-language', lang);

    // Update HTML lang attribute
    document.documentElement.lang = lang;

    // Update title and meta description
    updateMetaTags(lang);
  };

  const updateMetaTags = (lang: Language) => {
    // Type assertion to access the translation object
    const langTranslations = translations[lang] as any;
    const siteTitle = langTranslations?.site?.title || 'Monument Valley 3 Guide';
    const siteDescription = langTranslations?.site?.description || 'Complete walkthrough guide';
    const siteKeywords = langTranslations?.site?.keywords || 'Monument Valley 3, walkthrough';

    // Update page title
    document.title = siteTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', siteDescription);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', siteKeywords);
    }

    // Update og:title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', siteTitle);
    }

    // Update og:description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', siteDescription);
    }
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found in any language
          }
        }
        break;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  return (
    <I18nContext.Provider value={{
      language,
      setLanguage: handleSetLanguage,
      t,
      availableLanguages
    }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};